import { PROFILE_VIEW_SECTION } from '@workspace/seedforge-shared'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { BottomCta } from './components/bottom-cta.js'
import { Section } from './components/section.js'
import { DescriptionSection } from './sections/description.js'
import { DocumentsSection } from './sections/documents.js'
import { HeaderSection } from './sections/header.js'
import { PitchRecordingSection } from './sections/pitch-recording.js'
import { TeamSection } from './sections/team.js'
import type {
  DataRoomDocument,
  DataRoomFundraising,
  DataRoomPitchRecording,
  DataRoomProfile,
  DataRoomTeamMember,
  ProfileUpdate,
} from './types.js'

// ─── Types ─────────────────────────────────────────────────────────────────

/** Visitor instrumentation captured client-side and reported with each heartbeat. */
export interface HeartbeatInstrumentation {
  userAgent?: string
  browserTimezone?: string
  isWebdriver?: boolean
  referrer?: string
  viewportWidth?: number
  viewportHeight?: number
  screenWidth?: number
  screenHeight?: number
  language?: string
  firstInputAt?: Date
  inputCount?: number
}

export interface ProfileTrackingConfig {
  onHeartbeat?: (
    section: string,
    resourceId?: string,
    page?: number,
    totalPages?: number,
    instrumentation?: HeartbeatInstrumentation
  ) => void
  onEvent?: (type: string, resourceId?: string, metadata?: Record<string, unknown>) => void
  onDocumentOpen?: (documentId: string) => void | Promise<string | undefined>
  activeDocument?: string
  /** True while the pitch video preview modal is open. */
  activeVideo?: boolean
  /**
   * Id of the recording being watched, used to attribute viewing time per video.
   * Only meaningful while `activeVideo` is true — do not derive it from "this
   * profile has a pitch", which is true for the whole page life.
   */
  activeVideoId?: string
  /** Current page being viewed in the document previewer. */
  documentPage?: { page: number; totalPages: number }
  /** Called when the user navigates to a different page in the document previewer. */
  onDocumentPageChange?: (page: number, totalPages: number) => void
  /**
   * The inline deck's document id, if this profile has one inlined. While the
   * `deck` section (see `PROFILE_VIEW_SECTION.DECK`) is the one on screen, the
   * tracker attributes heartbeats to this document + `documentPage` instead of
   * a plain section-dwell row — reading the deck inline is the common case,
   * and without this its page-level reading time was never recorded at all
   * (only the modal previewer's `activeDocument` routed there).
   */
  deckDocumentId?: string
}

/**
 * The Profile: the founder's own material, measured data and the chat. It
 * renders no SeedForge judgment — no score, summary, dimensions, risks or
 * tensions. Those live on the founder's private SeedForge's Feedback page.
 */
export interface ProfileViewProps {
  profile: DataRoomProfile
  fundraising: DataRoomFundraising
  pitchRecordings: DataRoomPitchRecording[]
  selectedPitchId: string
  documents: DataRoomDocument[]
  updates?: ProfileUpdate[]
  team: DataRoomTeamMember[]
  tracking?: ProfileTrackingConfig
  hiddenSections?: Set<string>
  /** Beside the title in the profile's own header: the host app's buttons (an investor's Follow). */
  titleActions?: ReactNode
  onDocumentClick?: (doc: DataRoomDocument) => void
  // Public profile extras
  /**
   * When true, shows investor-facing elements (CTA, connect).
   * NOTE: this is NOT "is a share link" — the investor deal-flow route sets it
   * too, for logged-in investors.
   */
  isPublic?: boolean
  startupName?: string
  videoUrl?: string
  onConnect?: (message?: string) => void
  connectPending?: boolean
  onRequestDiligence?: () => void
  /** Share-link only: replaces the diligence upsell with the agent door. */
  onFollowCompany?: () => void
  onPlayVideo?: (currentTime: number) => void
  /** Offered in the Pitch Recording section when there is no recording yet. */
  pitchRecordingEmptyAction?: ReactNode
  /**
   * Deck rendered in the page, directly under the header. Supplied by the host
   * app because the PDF viewer lives there. When set it replaces the "Open
   * Pitch Deck" CTA in the description — the deck is already on screen.
   */
  deckPreview?: ReactNode
  /**
   * "Ask the profile" — the chat widget, rendered between the deck and the
   * description. App-owned like `deckPreview`: it needs `window` for the scroll
   * measurement that keeps its composer still, and putting it here would make
   * `@assistant-ui/*` a dependency of every consumer of this package.
   */
  chatWidget?: ReactNode
  /** The host shows its own way to follow (the app's Follow control): the closing "Interested in" block is left out. */
  hideBottomCta?: boolean
}

// ─── Component ─────────────────────────────────────────────────────────────

export function ProfileView({
  profile,
  fundraising,
  pitchRecordings,
  selectedPitchId,
  documents,
  updates = [],
  team,
  tracking,
  hiddenSections,
  titleActions,
  onDocumentClick,
  isPublic,
  startupName,
  videoUrl,
  onConnect,
  connectPending,
  onRequestDiligence,
  onFollowCompany,
  onPlayVideo,
  pitchRecordingEmptyAction,
  deckPreview,
  chatWidget,
  hideBottomCta,
}: ProfileViewProps) {
  const pitchRecording = pitchRecordings.find((p) => p.id === selectedPitchId) ?? pitchRecordings[0]

  const pitchDeck = useMemo(
    () => documents.find((d) => d.type === 'PITCH_DECK') ?? null,
    [documents]
  )

  const regularDocuments = useMemo(
    () => documents.filter((d) => d.type !== 'PITCH_DECK'),
    [documents]
  )

  const openDocument = (doc: DataRoomDocument) => {
    if (tracking?.onDocumentOpen) {
      tracking.onDocumentOpen(doc.documentId ?? doc.name)
    } else {
      onDocumentClick?.(doc)
    }
  }

  // Deliberately no data-section on the container: it wraps every section, so
  // it would always straddle the viewport anchor and beat the real one.
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-[#141414]">
      {/* 1. Header */}
      <HeaderSection
        profile={profile}
        fundraising={fundraising}
        isPublic={isPublic}
        actions={titleActions}
      />

      {/* Pitch deck, in the page. Deliberately above the description: the deck
          is what the founder sent the link for, and a click-through was costing
          them the reads. Marked `deck` so the tracker can tell it apart from a
          plain dwell-time section and route its heartbeats through the
          page-level document path instead (see useProfileTracker). */}
      {pitchDeck && deckPreview && (
        <Section dataSection={PROFILE_VIEW_SECTION.DECK}>{deckPreview}</Section>
      )}

      {/* Ask the profile. Deliberately no dataSection: `resolveActiveSection`
          assumes marked sections do not nest, and this one may render inside
          the deck section (DeckSlideRow) or standalone. */}
      {/* No `title`: the widget renders its own header row, because the section
          label and the sources panel have to sit on one line and only the
          widget knows what its sources are. */}
      {chatWidget && <Section>{chatWidget}</Section>}

      {/* 2. Description + Pitch Deck CTA (dropped once the deck is inlined).
          regularDocuments filters PITCH_DECK out of the documents section, so
          without an inlined deck this CTA is its only route. */}
      <DescriptionSection
        profile={profile}
        pitchDeck={deckPreview ? null : pitchDeck}
        onPitchDeckClick={pitchDeck ? () => openDocument(pitchDeck) : undefined}
      />

      {/* 3. Team */}
      <TeamSection
        team={team}
        onLinkedInClick={(memberName) => tracking?.onEvent?.('linkedin_click', memberName)}
      />

      {/* 4. Pitch Recording */}
      {!hiddenSections?.has('pitchRecordings') && (
        <PitchRecordingSection
          isPublic={isPublic}
          pitchRecording={pitchRecording}
          videoUrl={videoUrl}
          startupName={startupName ?? profile.name}
          emptyAction={pitchRecordingEmptyAction}
          onPlay={(currentTime) => {
            tracking?.onEvent?.('pitch_play', 'pitch', { currentTime })
            onPlayVideo?.(currentTime)
          }}
        />
      )}

      {/* 5. Documents (pitch deck shown above) */}
      <DocumentsSection
        documents={regularDocuments}
        updates={updates}
        onDocumentClick={openDocument}
      />

      {/* Bottom CTA — investor view */}
      {isPublic && startupName && !hideBottomCta && (
        <BottomCta
          startupName={startupName}
          onConnect={onConnect}
          connectPending={connectPending}
          onRequestDiligence={onRequestDiligence}
          onFollowCompany={onFollowCompany}
        />
      )}
    </div>
  )
}
